/**
 * Browser mic → mono linear16 PCM at 16 kHz for ai-gateway composer dictation.
 * Uses ScriptProcessor (widely supported). Does not log audio samples.
 */

const TARGET_SAMPLE_RATE = 16000;
/** ~100 ms of 16 kHz mono s16le. */
const CHUNK_SAMPLES = 1600;

export type PcmChunkHandler = (pcm: ArrayBuffer) => void;

export type PcmCaptureHandle = {
  stop: () => void;
};

function floatTo16BitPCM(
  input: Float32Array,
  output: Int16Array,
  offset: number,
): void {
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i] ?? 0));
    output[offset + i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
}

/** Downsample Float32 mono from `fromRate` to TARGET_SAMPLE_RATE. */
function resampleTo16k(input: Float32Array, fromRate: number): Float32Array {
  if (fromRate === TARGET_SAMPLE_RATE) return input;
  const ratio = fromRate / TARGET_SAMPLE_RATE;
  const outLen = Math.floor(input.length / ratio);
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const srcIndex = i * ratio;
    const i0 = Math.floor(srcIndex);
    const i1 = Math.min(i0 + 1, input.length - 1);
    const frac = srcIndex - i0;
    const a = input[i0] ?? 0;
    const b = input[i1] ?? 0;
    out[i] = a + (b - a) * frac;
  }
  return out;
}

/**
 * Start microphone capture. Calls `onChunk` with raw s16le mono PCM buffers.
 * Throws if permission is denied or AudioContext is unavailable.
 */
export async function startPcmCapture(
  onChunk: PcmChunkHandler,
): Promise<PcmCaptureHandle> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
    },
  });

  const AudioCtx =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext: typeof AudioContext })
      .webkitAudioContext;
  const ctx = new AudioCtx();
  const source = ctx.createMediaStreamSource(stream);

  // ScriptProcessor buffer size must be a power of 2.
  const processor = ctx.createScriptProcessor(4096, 1, 1);
  const pending: number[] = [];

  processor.onaudioprocess = (event) => {
    const input = event.inputBuffer.getChannelData(0);
    const resampled = resampleTo16k(input, ctx.sampleRate);
    for (let i = 0; i < resampled.length; i++) {
      pending.push(resampled[i] ?? 0);
    }
    while (pending.length >= CHUNK_SAMPLES) {
      const slice = pending.splice(0, CHUNK_SAMPLES);
      const floats = Float32Array.from(slice);
      const pcm = new Int16Array(floats.length);
      floatTo16BitPCM(floats, pcm, 0);
      onChunk(pcm.buffer.slice(0));
    }
  };

  const mute = ctx.createGain();
  mute.gain.value = 0;
  source.connect(processor);
  // Keep the processor alive in the graph without audible playback.
  processor.connect(mute);
  mute.connect(ctx.destination);

  let stopped = false;
  return {
    stop: () => {
      if (stopped) return;
      stopped = true;
      try {
        processor.disconnect();
        source.disconnect();
      } catch {
        /* already disconnected */
      }
      for (const track of stream.getTracks()) {
        track.stop();
      }
      void ctx.close();
    },
  };
}

export const PCM_SAMPLE_RATE = TARGET_SAMPLE_RATE;
